<template>
  <SrpModal :isVisible="props.isVisible" @close="emit('update:isVisible', false)">
    <template #content>
      <div class="spread-the-word-modal">
        <div class="header spread-the-word-modal__header">
          <h1 class="global-h1 header__title">Spread the Word!</h1>
          <div class="header__subtitle">Encourage uploads by sharing this link with locals, visitors, or businesses.</div>
        </div>

        <div class="spread-the-word-modal__divider"></div>

        <div class="spread-the-word-modal__link-row">
          <p class="spread-the-word-modal__link-text" :title="uploadLink">{{ uploadLink }}</p>
          <div class="spread-the-word-modal__copy-actions">
            <SrpButton size="normal" @click="copyUploadLink">Copy Link</SrpButton>
            <strong v-if="showCopiedFeedback" class="spread-the-word-modal__copied-label">Copied!</strong>
          </div>
        </div>

        <div class="spread-the-word-modal__divider"></div>

        <div class="spread-the-word-modal__share-content">
          <div class="spread-the-word-modal__ways">
            <div class="spread-the-word-modal__ways-title">Great ways to share:</div>
            <ul class="spread-the-word-modal__ways-list">
              <li class="spread-the-word-modal__ways-item">
                <img class="spread-the-word-modal__ways-icon" src="/images/otherIcons/like.svg" width="19" height="19" alt="" />
                <strong>Social Media</strong>
              </li>
              <li class="spread-the-word-modal__ways-item">
                <img class="spread-the-word-modal__ways-icon" src="/images/otherIcons/email.svg" width="19" height="19" alt="" />
                <strong>Email</strong>
              </li>
              <li class="spread-the-word-modal__ways-item">
                <img class="spread-the-word-modal__ways-icon" src="/images/otherIcons/globe.svg" width="19" height="19" alt="" />
                <strong>Website</strong>
              </li>
              <li class="spread-the-word-modal__ways-item spread-the-word-modal__qr-row">
                <img class="spread-the-word-modal__ways-icon" src="/images/otherIcons/qrcode.svg" width="19" height="19" alt="" />
                <strong>QR Code</strong>
              </li>
            </ul>
          </div>

          <div class="spread-the-word-modal__qr-result">
            <Loader v-if="isLoadingQRCode" />
            <template v-else-if="qrCodeImageData">
              <img class="spread-the-word-modal__qr-img" :src="`data:image/png;base64, ${qrCodeImageData}`" alt="QR code" />
              <div class="spread-the-word-modal__download-actions">
                <SrpButton size="small" fill="outlined" @click="downloadQrPng">Download</SrpButton>
              </div>
              <strong v-if="showDownloadedFeedback" class="spread-the-word-modal__copied-label">Downloaded!</strong>
            </template>
          </div>
        </div>
      </div>
    </template>

    <template #footer>
      <SrpButton @click="emit('update:isVisible', false)" fill="outlined">Done</SrpButton>
    </template>
  </SrpModal>
</template>

<script setup lang="ts">
import axios from "axios";
import { computed, onBeforeUnmount, ref, watch } from "vue";
import { Base64ToBinary } from "@logic/FileConversions";

import Loader from "@components/Loader/Loader.vue";
import SrpModal from "@components/ui/SrpModal.vue";
import SrpButton from "@components/ui/SrpButton.vue";

import { UserContentUploadCampaignData } from "@contracts/userContentUploadCampaignData";

const props = withDefaults(
  defineProps<{
    isVisible: boolean;
    pageId: string | null;
    customerUniqueName: string | null;
    campaign: UserContentUploadCampaignData | null;
  }>(),
  {
    isVisible: false,
    pageId: null,
    customerUniqueName: null,
    campaign: null,
  }
);

const emit = defineEmits<{
  (e: "update:isVisible", value: boolean): void;
}>();

const uploadLink = computed<string>(() => {
  return `${window.location.protocol}//${window.location.host}/upload/${props.customerUniqueName ?? props.campaign?.customerId}/${props.campaign?.uriKey || props.campaign?.campaignId}`;
});

const isLoadingQRCode = ref<boolean>(false);
const qrCodeImageData = ref<string>("");
const qrCodeBinaryData = ref<string | null>(null);

const showCopiedFeedback = ref(false);
let copiedFeedbackTimer: ReturnType<typeof setTimeout> | undefined;

function clearCopiedFeedbackTimer() {
  if (copiedFeedbackTimer !== undefined) {
    clearTimeout(copiedFeedbackTimer);
    copiedFeedbackTimer = undefined;
  }
}

const showDownloadedFeedback = ref(false);
let downloadedFeedbackTimer: ReturnType<typeof setTimeout> | undefined;

function clearDownloadedFeedbackTimer() {
  if (downloadedFeedbackTimer !== undefined) {
    clearTimeout(downloadedFeedbackTimer);
    downloadedFeedbackTimer = undefined;
  }
}

function revokeQrBlobUrl() {
  if (qrCodeBinaryData.value) {
    URL.revokeObjectURL(qrCodeBinaryData.value);
    qrCodeBinaryData.value = null;
  }
}

async function copyUploadLink() {
  try {
    await navigator.clipboard.writeText(uploadLink.value);
    clearCopiedFeedbackTimer();
    showCopiedFeedback.value = true;
    copiedFeedbackTimer = setTimeout(() => {
      showCopiedFeedback.value = false;
      copiedFeedbackTimer = undefined;
    }, 5000);
  } catch {
    // ignore
  }
}

function downloadQrPng() {
  if (!qrCodeBinaryData.value) return;
  const a = document.createElement("a");
  a.href = qrCodeBinaryData.value;
  a.download = `${props.customerUniqueName ?? "qrcode"}-${props.campaign?.uriKey ?? props.campaign?.campaignId}-qrcode.png`;
  a.rel = "noopener";
  a.click();
  clearDownloadedFeedbackTimer();
  showDownloadedFeedback.value = true;
  downloadedFeedbackTimer = setTimeout(() => {
    showDownloadedFeedback.value = false;
    downloadedFeedbackTimer = undefined;
  }, 5000);
}

onBeforeUnmount(() => {
  clearCopiedFeedbackTimer();
  clearDownloadedFeedbackTimer();
});

async function loadQRCode() {
  if (!props.pageId) return;

  revokeQrBlobUrl();
  qrCodeImageData.value = "";

  let uri = `${import.meta.env.VITE_API_URL}/qrcodes/${props.pageId}/community-upload`;
  const urlSearchParams = new URLSearchParams({ uri: uploadLink.value });
  uri = uri + "?" + urlSearchParams.toString();

  isLoadingQRCode.value = true;
  try {
    const { data } = await axios.get<string>(uri);
    qrCodeImageData.value = data;
    const binaryData = Base64ToBinary(qrCodeImageData.value);
    const blob = new Blob([binaryData as unknown as BlobPart], { type: "image/png" });
    qrCodeBinaryData.value = URL.createObjectURL(blob);
  } catch {
    // ignore
  } finally {
    isLoadingQRCode.value = false;
  }
}

watch(
  [() => props.isVisible, () => props.pageId, uploadLink],
  ([visible, pageId], previousValues) => {
    const previousVisible = previousValues?.[0] ?? false;

    if (visible && pageId) {
      void loadQRCode();
      return;
    }

    if (!visible && previousVisible) {
      revokeQrBlobUrl();
      qrCodeImageData.value = "";
      clearCopiedFeedbackTimer();
      showCopiedFeedback.value = false;
      clearDownloadedFeedbackTimer();
      showDownloadedFeedback.value = false;
    }
  },
  { immediate: true }
);
</script>

<style scoped lang="scss">
.header {
  &__title {
    margin-bottom: 4px;
  }
}

.spread-the-word-modal {
  color: rgba(91, 91, 91, 1);
  font: 14px/18px sans-serif;

  &__header {
    margin-bottom: 25px;
  }

  &__divider {
    margin-bottom: 20px;
    border-bottom: 1px rgba(0, 0, 0, 0.1) solid;
  }

  &__link-row {
    gap: 12px;
    display: flex;
    flex-wrap: wrap;
    align-items: center;
    justify-content: flex-start;
    padding-bottom: 20px;
  }

  &__link-text {
    flex: 0 1 auto;
    min-width: 0;
    max-width: 100%;
    margin: 0;
    padding: 0;
    color: rgba(0, 0, 0, 0.88);
    font: 15px/22px sans-serif;
    word-break: break-word;
    user-select: all;
  }

  &__copy-actions,
  &__download-actions {
    gap: 10px;
    display: inline-flex;
    flex-shrink: 0;
    align-items: center;
  }

  &__copied-label {
    font-weight: 700;
    font-family: "Quicksand", sans-serif;
    color: rgba(0, 0, 0, 0.75);
    font-size: 15px;
  }

  &__ways {
    margin-bottom: 8px;
  }

  &__share-content {
    gap: 24px;
    display: flex;
    align-items: flex-start;
    justify-content: flex-start;
  }

  &__ways-title {
    margin-bottom: 10px;
    font-weight: 600;
    color: rgba(91, 91, 91, 1);
  }

  &__ways-list {
    margin: 0;
    padding: 0;
    list-style: none;
  }

  &__ways-item {
    gap: 10px;
    display: flex;
    align-items: center;
    flex-wrap: wrap;
    margin-bottom: 10px;
    color: rgba(0, 0, 0, 0.85);
    font: 16px/26px sans-serif;

    &:last-child {
      margin-bottom: 0;
    }

    strong {
      font-weight: 700;
      font-family: "Quicksand", sans-serif;
    }
  }

  &__ways-icon {
    flex-shrink: 0;
    display: block;
    opacity: 0.85;
  }

  &__qr-row {
    align-items: center;
    gap: 10px;
  }

  &__qr-result {
    display: flex;
    flex-direction: column;
    align-items: center;
    flex: 1 1 auto;
    gap: 12px;
    min-width: 128px;
  }

  &__qr-img {
    margin-top: 18px;
    max-width: 120px;
    border: 1px #bbb solid;
    border-radius: 6px;
  }
}
</style>
